/**
 * Dialog de Impersonate — master admin only.
 *
 * Permite escolher um usuario alvo via Autocomplete server-side (debounce 250ms,
 * busca por nome/email/cpf). Ao confirmar, chama POST /api/auth/impersonate/:id,
 * substitui o accessToken em memoria + cookie de refresh httpOnly e faz hard
 * reload em "/", garantindo reset completo do estado React + TanStack Query.
 *
 * Reversao = logoff/login do master (decisao do produto: sem stack de identidades).
 *
 * Visivel apenas para usuarios isMasterAdmin = true.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, ListItem, ListItemAvatar, ListItemText, Stack, TextField, Typography,
} from "@mui/material";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";

interface UserOption {
  id: string;
  name: string;
  email: string;
  cpf: string;
  isMasterAdmin: boolean;
  status: string;
  institutions: { id: string; name: string }[];
}

interface Props {
  open: boolean;
  currentUserId: string | null;
  /** Quando informado, abre direto na etapa de confirmacao com este usuario
   *  pre-selecionado (botao "Impersonate" na linha da grid). */
  prefilled?: UserOption | null;
  onClose: () => void;
}

export function ImpersonateDialog({ open, currentUserId, prefilled, onClose }: Props) {
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<UserOption | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (!open) {
      setInput(""); setDebounced(""); setSelected(null); setConfirmStep(false);
    } else if (prefilled) {
      // Pre-fill quando vem da grid — pula direto pra etapa de confirmacao.
      setSelected(prefilled);
      setConfirmStep(true);
    }
  }, [open, prefilled]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  // Busca server-side. Mostra resultados depois que tem >=2 chars (evita carregar geral).
  const searchQ = useQuery({
    queryKey: ["impersonate-search", debounced],
    queryFn: async () => {
      const r = await api.get<{ data: UserOption[] }>("/users", {
        params: { q: debounced, page_size: 20 },
      });
      return r.data.data;
    },
    enabled: open && debounced.length >= 2,
  });

  const options = useMemo(
    () => (searchQ.data ?? []).filter((u) => u.id !== currentUserId && u.status === "active"),
    [searchQ.data, currentUserId],
  );

  const impersonateM = useMutation({
    mutationFn: async (id: string) => api.post<{ accessToken: string }>(`/auth/impersonate/${id}`),
    onSuccess: () => {
      // Hard reload em "/" — garante reset completo do estado (React Query cache,
      // contexto de auth, prefs de grid em memoria). O refresh cookie ja foi setado
      // pelo backend, entao o reload re-hidrata como o usuario impersonado.
      window.location.assign("/");
    },
  });

  return (
    <Dialog open={open} onClose={impersonateM.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SwitchAccountIcon color="primary" />
          <span>Impersonate</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {!confirmStep ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              Escolha um usuario para assumir a sessao. O sistema reagira como se ele
              tivesse feito o login. Para voltar ao seu acesso de administrador, faca
              logoff e login novamente.
            </DialogContentText>

            <Autocomplete<UserOption>
              options={options}
              loading={searchQ.isFetching}
              filterOptions={(x) => x}
              value={selected}
              onChange={(_, v) => setSelected(v)}
              inputValue={input}
              onInputChange={(_, v) => setInput(v)}
              noOptionsText={debounced.length < 2 ? "Digite ao menos 2 caracteres" : "Nenhum usuario encontrado"}
              getOptionLabel={(o) => `${o.name} (${o.email})`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, o) => {
                const { key, ...rest } = props as typeof props & { key?: string };
                return (
                  <ListItem key={o.id} {...rest} dense>
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32 }}>{o.name.charAt(0).toUpperCase()}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{o.name}</Typography>
                          {o.isMasterAdmin && <Chip size="small" color="primary" label="Master" />}
                        </Stack>
                      }
                      secondary={
                        <Stack component="span" direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                          {o.institutions.slice(0, 1).map((i) => (
                            <Chip key={i.id} size="small" label={i.name} sx={{ ml: 0.5 }} />
                          ))}
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              }}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Buscar por nome, email ou CPF"
                  autoFocus
                  InputProps={{
                    ...p.InputProps,
                    endAdornment: (
                      <>
                        {searchQ.isFetching ? <CircularProgress size={18} /> : null}
                        {p.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {impersonateM.isError && (
              <Alert severity="error">
                Falha ao impersonar. Verifique se o usuario esta ativo.
              </Alert>
            )}
            <Alert severity="warning">
              Voce esta prestes a assumir a sessao de outro usuario. A acao sera registrada
              em auditoria. Para voltar ao seu acesso administrativo, faca logoff e login.
            </Alert>
            {selected && (
              <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ width: 48, height: 48 }}>{selected.name.charAt(0).toUpperCase()}</Avatar>
                  <Box>
                    <Typography variant="subtitle1">{selected.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{selected.email}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                      {selected.isMasterAdmin && <Chip size="small" color="primary" label="Master admin" />}
                      {selected.institutions.map((i) => (
                        <Chip key={i.id} size="small" label={i.name} />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={impersonateM.isPending}>Cancelar</Button>
        {!confirmStep ? (
          <Button
            variant="contained"
            disabled={!selected}
            onClick={() => setConfirmStep(true)}
          >
            Continuar
          </Button>
        ) : (
          <Button
            variant="contained"
            color="warning"
            disabled={!selected || impersonateM.isPending}
            onClick={() => selected && impersonateM.mutate(selected.id)}
            startIcon={impersonateM.isPending ? <CircularProgress size={16} /> : <SwitchAccountIcon />}
          >
            {impersonateM.isPending ? "Aplicando..." : "Confirmar impersonate"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
